/**
 * Reusable PWA push enable/disable button used in the dashboard.
 */
import React, { useEffect, useState } from "react";
import { BellSimple, BellSimpleSlash, PaperPlaneTilt } from "@phosphor-icons/react";
import {
  pushSupported, registerServiceWorker, currentSubscription,
  subscribeForPush, unsubscribeFromPush, sendTestPush,
} from "@/lib/push";

export default function PushButton() {
  const [supported] = useState(pushSupported());
  const [subbed, setSubbed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!supported) return;
    registerServiceWorker().then(() => currentSubscription()).then((s) => setSubbed(!!s));
  }, [supported]);

  if (!supported) return null;

  const toggle = async () => {
    setBusy(true); setMsg("");
    try {
      if (subbed) {
        await unsubscribeFromPush();
        setSubbed(false);
        setMsg("Notifications turned off");
      } else {
        await subscribeForPush();
        setSubbed(true);
        setMsg("Notifications enabled on this device");
      }
    } catch (e) {
      setMsg(e.message || String(e));
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await sendTestPush();
      setMsg(`Push: ${data.successful}/${data.total} delivered`);
    } catch (e) { setMsg("Test failed: " + e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="push-toggle-btn"
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md transition-colors ${
          subbed
            ? "border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10"
            : "border-border hover:bg-white/5"
        }`}
        title={subbed ? "Disable push notifications" : "Enable push notifications"}
      >
        {subbed ? <BellSimple size={14} weight="fill" /> : <BellSimpleSlash size={14} />}
        {subbed ? "Push on" : "Enable push"}
      </button>
      {subbed && (
        <button onClick={test} disabled={busy} data-testid="push-test-btn"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs border border-border hover:bg-white/5 rounded-md text-slate-300"
          title="Send test notification">
          <PaperPlaneTilt size={12} weight="bold" />
        </button>
      )}
      {msg && <span className="text-[11px] text-slate-400 ml-1">{msg}</span>}
    </div>
  );
}
